import { StopType } from '../../../shared/stop-type.model';
import { LocalLocationType } from '../../../local-loads/shared/local-location-type';
import { Moment } from 'moment';
import * as moment from 'moment/moment';

const shortDateFormat = 'YYYY-MM-DD';

export enum AssignmentStatus {
  UNASSIGNED = 'UNASSIGNED',
  ASSIGNED = 'ASSIGNED',
  ALL = 'ALL'
}

// ------------ DELIVERIES ------------

export class TruckJourneyDeliveryEdit {
  public deliveryNumber: string;
  public salesOrderNumber: string;
  public pickStop: TruckJourneyStopEdit;
  public dropStop: TruckJourneyStopEdit;

  static fromDto(delivery: any) {
    return {
      ...delivery,
      pickStop: TruckJourneyStopEdit.fromDto(delivery.pickStop),
      dropStop: TruckJourneyStopEdit.fromDto(delivery.dropStop)
    };
  }
}

export class TruckJourneyDelivery {
  public number: string;
  public stopType: StopType;
  public salesOrderNumber: string;
}

// ------------ STOPS ------------
export class TruckJourneyStopRequest {
  public deliveryNumber: string;
  public stopId: string;
  public stopType: string;
  public rank: number
}

export class TruckJourneyStopEdit {
  public stopId: string;
  public locationLabel: string;
  public glid: string;
  public locationType: LocalLocationType;
  public plannedArrival: Moment;
  public plannedDeparture: Moment;

  static fromDto(dto: any) {
    if (!dto) {
      return null;
    }
    return {
      ...dto,
      plannedArrival: fromDateDto(dto.plannedArrival),
      plannedDeparture: fromDateDto(dto.plannedDeparture),
    };
  }
}

export class TruckJourneyStop extends TruckJourneyStopEdit {
  public deliveries: TruckJourneyDelivery[];

  static fromDto(dto: any) {
    if (!dto) {
      return null;
    }
    return {
      ...dto,
      plannedArrival: fromDateDto(dto.plannedArrival),
      plannedDeparture: fromDateDto(dto.plannedDeparture),
    };
  }
}

// ------------ LOADS ------------

export class TruckJourneyLoad {
  public loadId: string;
  public deliveryNumbers: string[];
  public stops: TruckJourneyStop[];

  static fromDto(dto: any) {
    if (!dto) {
      return null;
    }
    return {
      ...dto,
      stops: dto.stops.map((stop: any) => TruckJourneyStop.fromDto(stop))
    };
  }
}

export class TruckJourneyLoadEdit {
  public loadId: string;
  public deliveryNumbers: string[];
  public totalStops: number;
  public deliveries: TruckJourneyDeliveryEdit[];

  static fromDto(dto: any) {
    if (!dto) {
      return null;
    }
    return {
      ...dto,
      deliveries: dto.deliveries.map((delivery: any) => TruckJourneyDeliveryEdit.fromDto(delivery))
    };
  }
}

export class TruckJourneyAssignmentLoad extends TruckJourneyLoad {
  public status: AssignmentStatus;
  public vehicle: string;

  static fromDto(dto: any) {
    if (!dto) {
      return null;
    }
    return {
      ...dto,
      stops: dto.stops.map((stop: any) => TruckJourneyStop.fromDto(stop))
    };
  }
}

// ------------ TRUCK JOURNEYS ------------

export class TruckJourneyInfo { // Pour Search seulement
  public id: string;
  public vehicle: string;
  public fromLocation: string;
  public toLocation: string;
  public plannedPickupDate: Moment; // À virer peut-être, un simple getFirst de la FromPlannedDate du 1er stop peut suffire ?
  public totalStops: number;
  public loads: TruckJourneyLoad[];
  public rank: number;

  static fromDto(dto: any): TruckJourneyInfo {
    if (!dto) {
      return null;
    }
    return {
      ...dto,
      plannedPickupDate: fromDateDto(dto.plannedPickupDate),
      loads: dto.loads.map((load: any) => TruckJourneyLoad.fromDto(load))
    };
  }
}

export class TruckJourneyDetail { // Pour l'ecran d'edition de TruckJourney
  public id: string;
  public vehicle: string;
  public plannedPickupDate: Moment;
  public loads: TruckJourneyLoad[];
  public loadEdits: TruckJourneyLoadEdit[];
  public rank: number;

  static fromDto(dto: any): TruckJourneyDetail {
    if (!dto) {
      return null;
    }
    return {
      ...dto,
      plannedPickupDate: fromDateDto(dto.plannedPickupDate),
      loads: dto.loads.map((load: any) => TruckJourneyLoad.fromDto(load)),
      loadEdits: dto.loadEdits.map((load: any) => TruckJourneyLoadEdit.fromDto(load))
    };
  }

  static toDto(l): TruckJourneyDetail {
    return l;
  }
}

export interface TruckJourneyInfoOpenable extends TruckJourneyInfo {
  opened: boolean
}

// ------------ REQUESTS ------------

export class TruckJourneyRequest {
  public id: string;
  public carrierId: string;
  public vehicle: string;
  public plannedPickupDate: Moment;
  public loads: TruckJourneyLoadRequest[];
  public rank: number;

  static toDto(truckJourney: TruckJourneyRequest): any {
    return {...truckJourney,
      plannedPickupDate: truckJourney.plannedPickupDate ? truckJourney.plannedPickupDate.format(shortDateFormat) : undefined
    };
  }
}

export class TruckJourneyLoadRequest {
  public loadNumber: string;
  public stops: TruckJourneyStopRequest[];
}

export class TruckJourneyReorderRequest {
  public id: string;
  public rank: number;
}

export class TruckJourneysReorderRequest {
  public order: TruckJourneyReorderRequest[];
}

// ------------ CRITERIAS ------------

export class TruckJourneySearchCriteria {
  carrierId: string;
  loadNumber: string;
  vehicle: string;
  plannedDepartureFrom: Moment;
  plannedDepartureTo: Moment;
  pickLocationGlids: string[];
  dropLocationGlids: string[];

  static toDto(criteria: TruckJourneySearchCriteria): any {
    return {
      ...criteria,
      plannedDepartureFrom: criteria.plannedDepartureFrom.format(shortDateFormat),
      plannedDepartureTo: criteria.plannedDepartureTo.format(shortDateFormat),
    };
  }
}

export class TruckJourneyLoadSearchCriteria {
  carrierId: string;
  loadNumber: string;
  deliveryNumber: string;
  vehicle: string;
  excludedJourneyId?: string;
  appointmentPlannedFrom: Moment;
  appointmentPlannedTo: Moment;
  assignmentStatus: AssignmentStatus;
  pickLocationGlids: string[];
  dropLocationGlids: string[];

  static toDto(criteria: TruckJourneyLoadSearchCriteria): any {
    return {
      ...criteria,
      appointmentPlannedFrom: criteria.appointmentPlannedFrom.toISOString(),
      appointmentPlannedTo: criteria.appointmentPlannedTo.toISOString(),
    };
  }
}

export interface SearchValidity {
  isValid: boolean;
  color: string;
  messageKey: string;
}

export enum SearchValidityKey {
  IS_VALID = 'IS_VALID',
  NO_CARRIER_SELECTED = 'NO_CARRIER_SELECTED'
}

function fromDateDto(dateDto: any): Moment {
  return dateDto ? moment(dateDto) : null
}
